'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import CountryFAQ from '@/components/country/CountryFAQ';
import GfSectionMark from '@/components/common/GfSectionMark';
import { t } from '@/i18n/translations';

// Curated device list (mirrors the "Gallery · Field" reference). `ok` = eSIM-capable.
// Brand labels are localized via the esimToolsUi namespace (keyed by `n`).
const DEVICES = [
    { n: 'iPhone 17 / Air / Pro / Pro Max', ok: true },
    { n: 'iPhone 16 / Plus / Pro / 16e', ok: true },
    { n: 'iPhone 15 / Pro', ok: true },
    { n: 'iPhone 14 / Pro', ok: true },
    { n: 'iPhone 13 / Pro', ok: true },
    { n: 'iPhone 12 / Pro', ok: true },
    { n: 'iPhone 11 / XS / XR', ok: true },
    { n: 'iPhone SE (2020+)', ok: true },
    { id: 'iphone-8-older', n: 'iPhone 8 and older', ok: false },
    { n: 'Galaxy S24 / S23 / S22', ok: true },
    { n: 'Galaxy S21 / S20', ok: true },
    { n: 'Galaxy Z Fold / Flip', ok: true },
    { n: 'Galaxy A55 / A54', ok: true },
    { n: 'Galaxy A12 / A03', ok: false },
    { n: 'Pixel 8 / 7 / 6', ok: true },
    { n: 'Pixel 4 / 5', ok: true },
    { n: 'Pixel 3 / 3a', ok: true },
    { n: 'Xiaomi 14 / 13', ok: true },
    { id: 'xiaomi-redmi-most', n: 'Xiaomi Redmi (most models)', ok: false },
    { id: 'huawei-p40-mate-no-gms', n: 'Huawei P40 / Mate (without GMS)', ok: false },
];

const Check = () => (<svg viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>);
const Cross = () => (<svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" strokeLinejoin="round" /></svg>);

export default function ESimDeviceCheckPage({ lang = 'ru', country = 'montenegro', deviceCheckFaq = [] }) {
    const ui = t(lang, 'esimToolsUi').deviceCheck;
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(null);
    const resultRef = useRef(null);

    // Панель результата встаёт над списком моделей: после тапа по модели внизу списка она
    // оказывалась на −1000 px, и на телефоне ничего не происходило (обход 01.10.2026).
    useEffect(() => {
        const el = resultRef.current;
        if (!selected || !el) return;
        const r = el.getBoundingClientRect();
        if (r.top < 0 || r.bottom > window.innerHeight) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [selected]);

    const modelName = useCallback((d) => ui.models?.[d.id] || d.n, [ui.models]);
    const brandOf = useCallback((d) => ui.brand[d.id] || ui.brand[d.n] || '', [ui.brand]);
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return DEVICES;
        return DEVICES.filter((d) => modelName(d).toLowerCase().includes(q) || d.n.toLowerCase().includes(q) || brandOf(d).toLowerCase().includes(q));
    }, [brandOf, modelName, query]);

    const backHref = `/${lang}/${country}/esim`;
    const steps = ui.steps;

    const faqItems = (Array.isArray(deviceCheckFaq) ? deviceCheckFaq : [])
        .map((it) => ({ question: it.question ?? it.q, answer: it.answer ?? it.a }))
        .filter((it) => it.question && it.answer);

    return (
        <div className="gf-root">
            {/* ── MASTHEAD ─────────────────────────────────────── */}
            <section className="dc-mast">
                <Link className="dc-back" href={backHref}>{ui.back}</Link>
                <p className="dc-kick">{ui.kick}</p>
                <h1>{ui.title}</h1>
                <p className="sub">{ui.sub}</p>
            </section>

            {/* ── SEARCH ───────────────────────────────────────── */}
            <div className="dc-search">
                <div className="dc-field">
                    <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" strokeLinecap="round" /><path d="M21 21l-4.3-4.3" strokeLinecap="round" /></svg>
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
                        placeholder={ui.searchPlaceholder}
                        autoComplete="off"
                        aria-label={ui.searchAria}
                    />
                </div>
                <p className="dc-hint">{ui.searchHint}</p>
            </div>

            {/* ── RESULT ───────────────────────────────────────── */}
            {selected && (
                <div className="dc-result" ref={resultRef}>
                    <div className={`dc-rpanel ${selected.ok ? 'ok' : 'no'}`}>
                        <div className={`dc-badge ${selected.ok ? 'ok' : 'no'}`}>{selected.ok ? <Check /> : <Cross />}</div>
                        <div>
                            <div className="nm">{modelName(selected)}</div>
                            <div className="st">{selected.ok ? ui.supports : ui.notSupported}</div>
                        </div>
                        {selected.ok
                            ? <Link className="go" href={`/${lang}/${country}/esim#plans`}>{ui.choosePlan}</Link>
                            : <Link className="go" href={backHref}>{ui.alternatives}</Link>}
                    </div>
                </div>
            )}

            {/* ── DEVICE LIST ──────────────────────────────────── */}
            <section className="dc-sec">
                <GfSectionMark eyebrow={ui.popularModels} tag={ui.tapToCheck} />
                <div className="dc-grid">
                    {filtered.map((d) => (
                        <div className="dev" key={d.id || d.n} role="button" tabIndex={0}
                            onClick={() => setSelected(d)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(d); } }}>
                            <div>
                                <div className="dn">{modelName(d)}</div>
                                <div className="db">{brandOf(d)}</div>
                            </div>
                            <div className={`tick ${d.ok ? 'ok' : 'no'}`}>{d.ok ? <Check /> : <Cross />}</div>
                        </div>
                    ))}
                    {filtered.length === 0 && (
                        <div className="dc-empty">{ui.empty}</div>
                    )}
                </div>
            </section>

            {/* ── HOW TO CHECK ─────────────────────────────────── */}
            <section className="dc-sec" style={{ paddingTop: 0 }}>
                <GfSectionMark eyebrow={ui.manualCheck} tag={ui.twoWays} title={ui.manualTitle} />
                <p className="gf-lead">{ui.manualLead}</p>
                <div className="gf-steps">
                    {steps.map((s, i) => (
                        <div className="st" key={i}>
                            <div className="n">{s.n}</div>
                            <h3>{s.title}</h3>
                            <p>{s.d}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── FAQ ──────────────────────────────────────────── */}
            {faqItems.length > 0 && (
                <section className="dc-sec" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={ui.faqEyebrow} tag="FAQ" title={ui.faqTitle} />
                    <p className="gf-lead">{ui.faqLead}</p>
                    <CountryFAQ faqItems={faqItems} lang={lang} countrySlug={country} variant="gf" />
                </section>
            )}
        </div>
    );
}
